import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { config } from '../../config';
import {
    syncTimerAction,
    setIsRunning,
    setTimeLeft,
    setTimerStatus,
    setStartTime,
    setElapsedOffset,
    setTotalTime,
    startTimer,
    setError
} from '../../redux/Slices/timerSlice';
import { addActivityOptimistically } from '../../redux/Slices/activitiesSlice';


// Icons
import Play from '../icons/Play';
import Pause from '../icons/Pause';
import Stop from '../icons/Stop';
import Timer from '../icons/Timer';
import Countdown from '../icons/Countdown';
import History from '../icons/History';
import { formatDurationMs } from '../../utils/formatDuration';

const TimerItem = ({ cardId, timer, onShowActivities }) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.user);
    const intervalRef = useRef(null);
    const [cardInfo, setCardInfo] = useState(null);


    // Local inputs for countdown
    const [hours, setHours] = useState(0);
    const [minutes, setMinutes] = useState(30);
    const [seconds, setSeconds] = useState(0);

    const {
        mode,
        timeLeft,
        isRunning,
        totalTime,
        timerStatus,
        error,
        title,
        startTime,
        elapsedOffset,
        stopwatchElapsed,
        countdownElapsed,
        hydrated,
    } = timer;

    // --- Sync countdown inputs on mount ---
    useEffect(() => {
        if (mode === 'countdown' && timeLeft > 0 && !isRunning) {
            const { h, m, s } = formatDurationMs(timeLeft, { style: 'parts' });
            setHours(h);
            setMinutes(m);
            setSeconds(s);
        }
    }, [mode, timeLeft, isRunning]);

    // Timer update logic
    useEffect(() => {
        if (!isRunning || !startTime) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        const startTimeVal = startTime;
        const offset = elapsedOffset || 0;
        const duration = totalTime || 0;

        intervalRef.current = setInterval(() => {
            const now = Date.now();
            const sessionElapsedMs = Math.max(0, now - startTimeVal);

            if (mode === 'stopwatch') {
                dispatch(setTimeLeft({ cardId, timeLeft: offset + sessionElapsedMs }));
            } else {
                const remainingMs = duration - (offset + sessionElapsedMs);
                if (remainingMs <= 0) {
                    clearInterval(intervalRef.current);
                    dispatch(setIsRunning({ cardId, isRunning: false }));
                    dispatch(setTimeLeft({ cardId, timeLeft: 0 }));
                    dispatch(syncTimerAction({ cardId, action: 'stop', mode, duration, elapsed: 0, clickMs: now }));
                } else {
                    dispatch(setTimeLeft({ cardId, timeLeft: remainingMs }));
                }
            }
        }, 1000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isRunning, startTime, elapsedOffset, totalTime, mode, cardId, dispatch]);

    // Live value at click — avoids the 1s discretization of timer.timeLeft.
    const liveTimeLeftAt = (clickMs) => {
        if (!isRunning || !startTime) return timeLeft;
        const sessionMs = Math.max(0, clickMs - startTime);
        const base = (elapsedOffset || 0) + sessionMs;
        if (mode === 'stopwatch') return base;
        return Math.max(0, (totalTime || 0) - base);
    };

    const handlePause = async () => {
        const clickMs = Date.now();
        const liveTimeLeft = liveTimeLeftAt(clickMs);

        // Flush a render while status is still 'running' so displayTotalRef
        // captures Date.now() at click (same rationale as handleStop).
        flushSync(() => {
            dispatch(setTimeLeft({ cardId, timeLeft: liveTimeLeft }));
        });

        dispatch(setIsRunning({ cardId, isRunning: false }));
        dispatch(setTimerStatus({ cardId, status: 'paused' }));
        clearInterval(intervalRef.current);

        try {
            await dispatch(syncTimerAction({
                cardId,
                action: 'pause',
                mode,
                duration: totalTime,
                elapsed: liveTimeLeft,
                clickMs
            })).unwrap();

            if (user?.user) {
                dispatch(addActivityOptimistically({
                    cardId,
                    userId: user.user._id,
                    actionType: 'paused timer',
                    details: `Paused the **${mode}** at **${formatDurationMs(liveTimeLeft)}**`,
                    user: user.user
                }));
            }
        } catch (error) {
            console.error('Failed to pause timer:', error);
        }


    };

    const handleResume = async () => {
        if (isRunning) return;

        const duration = (hours * 3600 + minutes * 60 + seconds) * 1000;

        if (mode === 'countdown' && duration <= 0) {
            dispatch(setError({ cardId, error: 'Please set a valid duration for countdown' }));
            return;
        }

        const clickMs = Date.now();

        dispatch(setIsRunning({ cardId, isRunning: true }));
        dispatch(setTimerStatus({ cardId, status: 'running' }));
        dispatch(setTotalTime({ cardId, totalTime: duration }));
        dispatch(setStartTime({ cardId, startTime: clickMs }));

        if (mode === 'countdown') {
            dispatch(setTimeLeft({ cardId, timeLeft: duration }));
        }

        try {

            const elapsed =
                mode === 'countdown'
                    ? countdownElapsed
                    : stopwatchElapsed;

            const actionResult = await dispatch(syncTimerAction({ cardId, action: 'resume', mode, duration, elapsed, clickMs })).unwrap();

            if (user?.user) {
                const actionText = timeLeft === 0 ? 'started timer' : 'resumed timer';
                const detailText = timeLeft === 0
                    ? `Started a **${mode}** session on this card`
                    : `Resumed the **${mode}** from **${formatDurationMs(timeLeft)}**`;

                dispatch(addActivityOptimistically({
                    cardId,
                    userId: user.user._id,
                    actionType: actionText,
                    details: detailText,
                    user: user.user
                }));
            }

            const serverElapsed =
                mode === 'countdown'
                    ? actionResult.countdownElapsed
                    : actionResult.stopwatchElapsed;
            dispatch(setElapsedOffset({ cardId, elapsedOffset: serverElapsed }));
        } catch (err) {
            dispatch(setError({ cardId, error: err.message || 'Failed to resume timer' }));
        }
    };

    const handleStop = async () => {
        const clickMs = Date.now();
        const liveTimeLeft = liveTimeLeftAt(clickMs);

        // Force a synchronous render while status is still 'running' so card
        // consumers (CardDetailsFullLayout's displayTotalRef) capture Date.now()
        // at click rather than the last tick's stale value.
        flushSync(() => {
            dispatch(setTimeLeft({ cardId, timeLeft: liveTimeLeft }));
        });

        dispatch(setIsRunning({ cardId, isRunning: false }));
        dispatch(setTimerStatus({ cardId, status: 'stopped' }));
        dispatch(setTimeLeft({ cardId, timeLeft: 0 }));
        dispatch(setStartTime({ cardId, startTime: null }));
        dispatch(setElapsedOffset({ cardId, elapsedOffset: 0 }));
        dispatch(setTotalTime({ cardId, totalTime: 0 }));
        clearInterval(intervalRef.current);

        try {
            await dispatch(syncTimerAction({
                cardId,
                action: 'stop',
                mode,
                duration: totalTime,
                elapsed: 0,
                clickMs
            })).unwrap();

            if (user?.user) {
                dispatch(addActivityOptimistically({
                    cardId,
                    userId: user.user._id,
                    actionType: 'stopped timer',
                    details: `Finished **${mode}** session at **${formatDurationMs(liveTimeLeft)}**`,
                    user: user.user
                }));

            }
        } catch (error) {
            console.error('Failed to stop timer:', error);
        }


    };


    if (!timer) {
        return (
            <div className="timer-item loading">
                <div className="timer-item-content">
                    <div className="timer-item-info">
                        <div className="card-title-skeleton"></div>
                        <div className="timer-display-skeleton"></div>
                    </div>
                </div>
            </div>
        );
    }



    return (
        <div className={`timer-item ${timerStatus}`} >
            <div className="timer-item-content">

                <Link to={`/c/${cardId}`} className="card-title">
                    {title || ''}
                </Link>

                <div className="timer-item-info">
                    <div className="timer-display">
                        <div className="timer-mode-icon">
                            {mode === 'countdown' ? (
                                <Countdown size={14} color="#10B981" />
                            ) : (
                                <Timer size={14} color="#10B981" />
                            )}
                        </div>
                        <span className="timer-time">{formatDurationMs(timeLeft)}</span>
                    </div>

                    <div className="timer-item-controls">
                        <button
                            className="timer-activities-btn"
                            onClick={() => onShowActivities(cardId)}
                            data-tooltip-content="View Activities"
                            data-tooltip-position="top"
                        >
                            <History size={14} />
                        </button>

                        {isRunning ? (
                            <button
                                className="timer-pause-btn"
                                onClick={handlePause}
                                title="Pause"
                                data-tooltip-content="Pause"
                                data-tooltip-position="top"
                            >
                                <Pause size={14} color="#10B981" />
                            </button>
                        ) : (
                            <button
                                className="timer-play-btn"
                                onClick={handleResume}
                                data-tooltip-content="Resume"
                                data-tooltip-position="top"
                            >
                                <Play size={14} color="#F59E0B" />
                            </button>
                        )}

                        <button
                            className="timer-stop-btn"
                            onClick={handleStop}
                            data-tooltip-content="Stop"
                            data-tooltip-position="top"
                            disabled={timeLeft === 0}
                        >
                            <Stop size={14} color="#EF4444" />
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default TimerItem;
